import { useState, useRef, RefObject } from "react";
import {
  Button,
  Modal,
  DateInput,
  TextAreaInput,
  Grid,
} from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { z } from "zod";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import ButtonMenu from "@/components/Forms/components/ButtonMenu.js";

import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import { getIpAddress } from "@/api";
import { db_timestamp } from "@/utils/date-format";

import type { BaseCocModalProps, CocSignatureObject } from "../Coc.types";
import EvidenceCocAPI from "@/api/evidence/coc";
import { createSignature } from "../utils";
import { SignatureField, SignatureFieldRef } from "@/Features";

interface CheckoutCocModalProps extends BaseCocModalProps {}

interface CheckoutFormData {
  reason: string;
  timestamp: string;
}

const checkoutValidationSchema = z.object({
  reason: z.string().optional(),
  timestamp: z
    .string({
      required_error: "Timestamp is required",
    })
    .min(1, { message: "Timestamp is required" }),
});

const CheckoutCocModal: React.FC<CheckoutCocModalProps> = ({
  show,
  onClose,
  onSubmit,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<CheckoutFormData>({
    mode: "uncontrolled",
    initialValues: {
      reason: "",
      timestamp: new Date().toISOString(),
    },
  });

  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const checkoutToSig = useRef<SignatureFieldRef>();

  const handleSubmit = async () => {
    const formData = form.getValues();

    const validationResult = await validateAsync(checkoutValidationSchema)(
      formData
    );
    if (validationResult?.hasErrors) {
      console.error("Validation Errors", validationResult?.errors);
      form.setErrors(validationResult?.errors);
      return;
    }

    setSubmitting(true);
    try {
      const ipAddress = await getIpAddress();

      const signature: CocSignatureObject = {
        custody_to_sig: createSignature(
          checkoutToSig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
      };

      const requestObj = {
        caseInfo: {
          case_id: defaultInfo.case_id,
        },
        evidenceInfo: {
          evidence_id: defaultInfo.evidence_id,
          uuid: defaultInfo.uuid,
          evidence_number: defaultInfo.evidence_number,
        },
        moveData: {
          entered_by_id: currentUser.user_id,
          entered_by: currentUser.full_name,
          new_location_id: currentUser.location_id,
          new_location_name: currentUser.full_name,
          current_location_name: defaultInfo.location_name,
          reason: formData.reason || "Evidence checked out by user",
          timestamp: db_timestamp(formData.timestamp, true),
          signature: JSON.stringify(signature),
          coc_type: "Checkout",
        },
      };

      await EvidenceCocAPI.moveEvidence(requestObj);

      enqueueSnackbar(`Evidence Item Checked Out`, {
        variant: "success",
      });

      onSubmit?.(requestObj);
    } catch (error) {
      console.error("Error checking out evidence:", error);
      enqueueSnackbar(`Error checking out evidence item.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose?.();
  };

  return (
    <Modal open={show} onClose={onClose} style={{ width: 600 }}>
      <Modal.Title>Checkout Evidence Item</Modal.Title>
      <div style={{ margin: "20px 0px" }}>
        <Grid col={1}>
          <DateInput
            label="Timestamp"
            required
            format="YYYY-MM-DD HH:mm:ss"
            enableCalendar
            arrow
            key={form.key("timestamp")}
            {...form.getInputProps("timestamp")}
          />
          <TextAreaInput
            label="Checkout Notes"
            minRows={3}
            key={form.key("reason")}
            {...form.getInputProps("reason")}
          />
          <div>
            <SignatureField
              sigFieldRef={checkoutToSig}
              label="Checked Out By Signature"
              useESignature={true}
            />
          </div>
        </Grid>
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Checkout Evidence
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default CheckoutCocModal;
